import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FinalCta from "@/components/FinalCta";
import AcademyCityCta from "@/components/AcademyCityCta";
import { useSite } from "@/context/SiteContext";
import { CITIES, findCity } from "@/lib/cities";
import NotFound from "@/pages/NotFound";

/** Live conditions via Open-Meteo (free, keyless). Hidden entirely if the fetch fails. */
function CityWeather({ city }) {
  const [wx, setWx] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${city.lat}&longitude=${city.lng}&current=temperature_2m,cloud_cover,wind_speed_10m`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("weather"))))
      .then((d) => {
        if (alive && d?.current) setWx(d.current);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [city.lat, city.lng]);
  if (!wx) return null;
  const sky = wx.cloud_cover <= 25 ? "Mostly sunny" : wx.cloud_cover <= 65 ? "Partly cloudy" : "Cloudy";
  return (
    <div className="city-weather" data-testid="city-weather">
      <div>
        <span className="eyebrow">IN {city.name.toUpperCase()} NOW</span>
        <strong data-testid="city-temp">{Math.round(wx.temperature_2m)}°C / {Math.round(wx.temperature_2m * 9 / 5 + 32)}°F</strong>
        <small>Live via Open-Meteo</small>
      </div>
      <div>
        <span className="eyebrow">SKY</span>
        <strong>{sky}</strong>
        <small>{Math.round(wx.cloud_cover)}% cloud cover</small>
      </div>
      <div>
        <span className="eyebrow">WIND</span>
        <strong>{Math.round(wx.wind_speed_10m * 0.6214)} mph</strong>
        <small>Surface wind</small>
      </div>
    </div>
  );
}

export default function CityPage() {
  const { city: slug } = useParams();
  const city = findCity(slug);
  const { openAudit } = useSite();

  useEffect(() => {
    if (!city) return undefined;
    const node = document.createElement("script");
    node.type = "application/ld+json";
    node.id = "city-jsonld";
    node.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `https://commercialsolarroi.com${city.path}#service`,
          "serviceType": "Commercial solar installation and utility-bill review",
          "provider": { "@id": "https://commercialsolarroi.com/#organization" },
          "areaServed": { "@type": "City", name: city.name, addressRegion: "FL", addressCountry: "US" },
          "url": `https://commercialsolarroi.com${city.path}`,
        },
        {
          "@type": "WebPage",
          "@id": `https://commercialsolarroi.com${city.path}#webpage`,
          "url": `https://commercialsolarroi.com${city.path}`,
          name: `${city.name} Commercial Solar Guide`,
          "isPartOf": { "@id": "https://commercialsolarroi.com/#website" },
          "about": { "@id": "https://commercialsolarroi.com/#organization" },
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://commercialsolarroi.com/" },
            { "@type": "ListItem", "position": 2, "name": "Florida Commercial Solar Overview", "item": "https://commercialsolarroi.com/florida-commercial-solar" },
            { "@type": "ListItem", "position": 3, "name": `${city.name} Commercial Solar Guide`, "item": `https://commercialsolarroi.com${city.path}` },
          ],
        },
      ],
    });
    document.head.appendChild(node);
    return () => {
      document.getElementById("city-jsonld")?.remove();
    };
  }, [city]);

  if (!city) return <NotFound />;
  return (
    <>
      <Seo
        title={`Commercial Solar in ${city.name}, FL | Solar Black Belt`}
        description={`Commercial solar and storage for ${city.name} businesses: ${city.county} sun, utility and weather context, then a bill-based review. Upload your electric bill or call 310-881-5155.`}
      />
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">WE INSTALL COMMERCIAL SOLAR · {city.name.toUpperCase()}</span>
          <h1 data-testid="city-heading">Commercial solar for {city.name} businesses.</h1>
          <p className="lead">{city.blurb}</p>
          <div style={{ display: "flex", gap: 14, marginTop: 26, flexWrap: "wrap" }}>
            <button className="btn lime" data-testid="city-upload-cta" onClick={() => openAudit()}>
              Upload my electric bill <span>↗</span>
            </button>
            <Link className="btn light" to="/projects" data-testid="city-jobs-link">
              See jobs we've done ↗
            </Link>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 40 }}>
        <div className="wrap city-grid">
          <div>
            <Reveal>
              <h2 style={{ fontSize: 24, margin: "0 0 10px" }}>Sun &amp; weather in {city.name}</h2>
              <CityWeather city={city} />
              <p className="muted" style={{ fontSize: 13, maxWidth: 640 }}>
                The typical year in {city.county} runs on two seasons — a long dry, sunny stretch and a hot, stormy wet season.
                Solar production is steadiest in the dry months; air-conditioning demand peaks in the wet ones. For the published
                long-range outlooks, the <b>Farmers' Almanac</b> tracks this region directly:
              </p>
              <ul className="benefitlist" data-testid="city-almanac-links">
                <li>
                  <a href="https://www.farmersalmanac.com/long-range-weather-forecast/southeast-us" target="_blank" rel="noreferrer">
                    Farmers' Almanac — Southeast U.S. long-range forecast ↗
                  </a>
                </li>
                <li>
                  <a href="https://www.farmersalmanac.com/extended-forecast" target="_blank" rel="noreferrer">
                    Farmers' Almanac — winter weather outlook ↗
                  </a>
                </li>
                <li>
                  <a href="https://www.farmersalmanac.com/summer-extended-forecast" target="_blank" rel="noreferrer">
                    Farmers' Almanac — summer outlook ↗
                  </a>
                </li>
              </ul>
              <p className="micro">Links go to farmersalmanac.com. Their full year-by-year day forecasts are published in the annual edition; we link rather than reprint.</p>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>The {city.name} year, season by season</h2>
              <dl data-testid="city-seasons">
                {city.seasons.map(([s, d]) => (
                  <div key={s} style={{ marginBottom: 10 }}>
                    <dt style={{ fontWeight: 800, fontSize: 14 }}>{s}</dt>
                    <dd style={{ margin: "2px 0 0", fontSize: 13.5, color: "var(--muted)" }}>{d}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>Utilities in {city.name}</h2>
              <p className="muted" style={{ fontSize: 13.5, maxWidth: 640 }}>{city.utilities}</p>
              <p className="micro">Your bill's header names your exact provider and rate schedule — that is what the review is built from.</p>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>Guides {city.name} buyers use next</h2>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }} data-testid="city-related-guides">
                <Link className="btn light" to="/florida-commercial-solar">Florida Commercial Solar Overview</Link>
                <Link className="btn light" to="/florida-commercial-solar/tax-incentives">Florida Commercial Solar Tax Incentives</Link>
                <Link className="btn light" to="/florida-commercial-solar/battery-demand-charges">Battery Storage and Demand Charges</Link>
                {city.slug === "miami" || city.slug === "fort-lauderdale" || city.slug === "west-palm-beach" ? (
                  <Link className="btn light" to="/florida-commercial-solar/regions/miami">Miami Commercial Solar Guide</Link>
                ) : city.slug === "tampa" || city.slug === "st-petersburg" ? (
                  <Link className="btn light" to="/florida-commercial-solar/regions/tampa-bay">Tampa Bay Commercial Solar Guide</Link>
                ) : (
                  <Link className="btn light" to="/florida-commercial-solar/regions/orlando">Orlando Commercial Solar Guide</Link>
                )}
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>{city.name} questions</h2>
              <div data-testid="city-faq">
                {city.faqs.map(([q, a]) => (
                  <details key={q}>
                    <summary>{q}</summary>
                    <p>{a}</p>
                  </details>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <aside className="report-preview" style={{ position: "sticky", top: 90 }}>
              <span className="eyebrow">START WITH YOUR BILL</span>
              <h3>The fastest route to real numbers.</h3>
              <p style={{ fontSize: 13.5, color: "var(--muted)", lineHeight: 1.7 }}>
                Upload a recent {city.name} utility bill — PDF or a clear photo. The project team reviews it manually and follows
                up by phone or email with next steps and a timeline. No payment, no automated guesswork.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
                <button className="btn" data-testid="city-aside-upload" onClick={() => openAudit()}>
                  Upload my electric bill <span>↗</span>
                </button>
                <Link className="btn light" to="/#roi">
                  Try the calculator first ↓
                </Link>
                <Link className="btn light" to="/about">
                  Who we are ↗
                </Link>
              </div>
              <p className="micro" style={{ marginTop: 14 }}>Also serving: {CITIES.filter((c) => c.slug !== city.slug).slice(0, 5).map((c) => c.name).join(" · ")}</p>
            </aside>
          </Reveal>
        </div>
      </section>
      <AcademyCityCta city={city} />
      <FinalCta title={[`Let's talk`, `${city.name}.`]} service="commercial" context={`${city.name} quote request`} />
    </>
  );
}
